/**
 * CacheService - Hybrid Caching with localStorage for Critical Data
 *
 * This service provides a hybrid caching solution:
 * 1. Critical App Data - Stored in localStorage for persistence across sessions
 * 2. Performance Cache - Stored in memory with LRU eviction for API responses
 *
 * Benefits:
 * - Critical data persists across browser sessions
 * - Performance data remains fast with memory storage
 * - Automatic cleanup of expired items
 * - Fallback to memory if localStorage is unavailable
 */
class CacheService {
  constructor() {
    // Critical app data - stored in localStorage for persistence
    this.localStoragePrefix = 'chillfree_cache_';
    this.localStorageAvailable = this.checkLocalStorageAvailability();

    // Performance cache - LRU eviction for API responses and temporary data
    this.performanceCache = new Map();
    this.maxPerformanceItems = 200;

    // Track access order for LRU eviction
    this.accessOrder = [];

    // Define cache strategies
    this.cacheStrategies = {
      // Critical app data - stored in localStorage
      APP_LOAD: 'critical',
      TOKEN: 'critical',
      ACCESS_TOKEN: 'critical',
      API_URL: 'critical',
      IMAGE_CDN: 'critical',
      USER: 'critical',
      CLIENT_TOKEN: 'critical',
      CLIENT_TOKEN_EXPIRY: 'critical',
      PROFILE_TOKEN: 'critical',
      PROFILE_TOKEN_EXPIRY: 'critical',
      AUTH: 'critical',
      CONFIG: 'critical',
      PROFILE: 'critical',
      CURRENT_PROFILE: 'critical',

      // Performance data - stored in memory
      CHANNEL_DETAILS: 'performance',
      MOVIE_DETAILS: 'performance',
      SERIES_DETAILS: 'performance',
      MOVIES_CATEGORIES: 'performance',
      SERIES_CATEGORIES: 'performance',
      CATEGORY_CHANNELS: 'performance',
      RELATED_CHANNELS: 'performance',
      SEARCH_RESULTS: 'performance',
      EPG_DATA: 'performance',
      SPOTLIGHT_DATA: 'performance',
    };

    // Different TTLs for different data types
    this.ttlDefaults = {
      // Critical data - longer TTLs
      APP_LOAD: 60, // 1 hour
      TOKEN: 1440, // 24 hours
      ACCESS_TOKEN: 1440, // 24 hours
      API_URL: 1440, // 24 hours
      IMAGE_CDN: 1440, // 24 hours
      USER: 1440, // 24 hours
      CLIENT_TOKEN: 10080, // 7 days
      CLIENT_TOKEN_EXPIRY: 10080, // 7 days
      PROFILE_TOKEN: 10080, // 7 days
      PROFILE_TOKEN_EXPIRY: 10080, // 7 days
      AUTH: 1440, // 24 hours
      CONFIG: 1440, // 24 hours
      PROFILE: 720, // 24 hours
      CURRENT_PROFILE: 720, // 24 hours

      // Performance data - shorter TTLs
      CHANNEL_DETAILS: 10, // 10 minutes
      MOVIE_DETAILS: 10, // 10 minutes
      SERIES_DETAILS: 10, // 10 minutes
      CATEGORY_CHANNELS: 5, // 5 minutes
      RELATED_CONTENT: 5, // 5 minutes
      SEARCH_RESULTS: 2, // 2 minutes
      EPG_DATA: 5, // 5 minutes
      SPOTLIGHT_DATA: 5, // 5 minutes
    };

    // Load critical data from localStorage on initialization
    this.loadCriticalDataFromStorage();

    // Periodic cleanup
    this.setupPeriodicCleanup();
  }

  /**
   * Check if localStorage is available and working
   */
  checkLocalStorageAvailability() {
    try {
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      console.warn('localStorage not available, falling back to memory storage for critical data');
      return false;
    }
  }

  /**
   * Load critical data from localStorage on initialization
   * Also checks sessionStorage as fallback for Safari
   */
  loadCriticalDataFromStorage() {
    if (!this.localStorageAvailable) {
      // Try to load from sessionStorage as fallback
      console.log('🦁 Safari: localStorage unavailable, checking sessionStorage...');
      this.loadFromSessionStorage();
      return;
    }

    try {
      const criticalKeys = Object.keys(localStorage).filter(key =>
        key.startsWith(this.localStoragePrefix),
      );

      let cleanedCount = 0;
      criticalKeys.forEach(storageKey => {
        try {
          const storedData = localStorage.getItem(storageKey);

          if (storedData) {
            const item = JSON.parse(storedData);

            // Check if item is expired and clean up
            const isExpired = Date.now() - item.timestamp > item.ttl;
            if (isExpired) {
              localStorage.removeItem(storageKey);
              cleanedCount++;
            }
          }
        } catch (e) {
          console.warn(`Failed to load critical data for key: ${storageKey}`, e);
          // Remove corrupted data
          localStorage.removeItem(storageKey);
          cleanedCount++;
        }
      });
    } catch (e) {
      console.error('Failed to load critical data from localStorage:', e);
    }
  }

  /**
   * Save critical data to localStorage
   */
  saveCriticalDataToStorage(key, item) {
    if (!this.localStorageAvailable) {
      // Try sessionStorage as fallback for Safari
      this.trySessionStorageFallback(key, item);
      return;
    }

    try {
      const storageKey = this.localStoragePrefix + key;
      localStorage.setItem(storageKey, JSON.stringify(item));
    } catch (e) {
      console.warn(`Failed to save critical data to localStorage for key: ${key}`, e);
      
      // Safari-specific: Try sessionStorage as fallback
      if (e.name === 'QuotaExceededError') {
        console.log('🦁 Safari: localStorage quota exceeded, trying sessionStorage fallback...');
        const sessionSuccess = this.trySessionStorageFallback(key, item);
        
        if (!sessionSuccess) {
          // If both fail, mark localStorage as unavailable and keep in memory only
          console.warn('🦁 Safari: Both localStorage and sessionStorage failed, using memory only');
          this.localStorageAvailable = false;
        }
      } else {
        // For other errors, fall back to memory only
        this.localStorageAvailable = false;
      }
    }
  }

  /**
   * Try to save critical data to sessionStorage as fallback for Safari
   * @param {string} key - Cache key
   * @param {*} item - Data to store
   * @returns {boolean} True if successful, false otherwise
   */
  trySessionStorageFallback(key, item) {
    try {
      const storageKey = this.localStoragePrefix + key;
      sessionStorage.setItem(storageKey, JSON.stringify(item));
      console.log(`🦁 Safari: Saved ${key} to sessionStorage as fallback`);
      return true;
    } catch (sessionError) {
      console.error(`🦁 Safari: sessionStorage fallback also failed for ${key}:`, sessionError);
      return false;
    }
  }

  /**
   * Load critical data from sessionStorage (Safari fallback)
   */
  loadFromSessionStorage() {
    try {
      const criticalKeys = Object.keys(sessionStorage).filter(key =>
        key.startsWith(this.localStoragePrefix),
      );

      console.log(`🦁 Safari: Found ${criticalKeys.length} items in sessionStorage`);
      
      criticalKeys.forEach(storageKey => {
        try {
          const storedData = sessionStorage.getItem(storageKey);
          if (storedData) {
            const item = JSON.parse(storedData);
            // Check if item is expired
            const isExpired = Date.now() - item.timestamp > item.ttl;
            if (!isExpired) {
              // Load into performance cache (memory)
              const key = storageKey.replace(this.localStoragePrefix, '');
              this.performanceCache.set(key, item);
            } else {
              sessionStorage.removeItem(storageKey);
            }
          }
        } catch (e) {
          console.warn(`Failed to load from sessionStorage for key: ${storageKey}`, e);
          sessionStorage.removeItem(storageKey);
        }
      });
    } catch (e) {
      console.error('🦁 Safari: Failed to load from sessionStorage:', e);
    }
  }

  /**
   * Get item from sessionStorage (Safari fallback)
   * @param {string} key - Cache key
   * @returns {*} Cached data or null
   */
  getFromSessionStorage(key) {
    try {
      const storageKey = this.localStoragePrefix + key;
      const storedData = sessionStorage.getItem(storageKey);

      if (!storedData) {
        return null;
      }

      const item = JSON.parse(storedData);

      // Check expiration
      const isExpired = Date.now() - item.timestamp > item.ttl;
      if (isExpired) {
        sessionStorage.removeItem(storageKey);
        return null;
      }

      return item.data;
    } catch (e) {
      console.warn(`🦁 Safari: Failed to get from sessionStorage for key: ${key}`, e);
      return null;
    }
  }

  /**
   * Remove critical data from localStorage
   */
  removeCriticalDataFromStorage(key) {
    if (!this.localStorageAvailable) return;

    try {
      const storageKey = this.localStoragePrefix + key;
      localStorage.removeItem(storageKey);
    } catch (e) {
      console.warn(`Failed to remove critical data from localStorage for key: ${key}`, e);
    }
  }

  /**
   * Clear all critical data from localStorage
   */
  clearCriticalDataFromStorage() {
    if (!this.localStorageAvailable) return;

    try {
      const criticalKeys = Object.keys(localStorage).filter(key =>
        key.startsWith(this.localStoragePrefix),
      );

      criticalKeys.forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (e) {
      console.error('Failed to clear critical data from localStorage:', e);
    }
  }

  /**
   * Determine cache strategy based on key
   */
  getCacheStrategy(key) {
    for (const [prefix, strategy] of Object.entries(this.cacheStrategies)) {
      if (key.includes(prefix)) {
        return strategy;
      }
    }
    return 'performance'; // Default to performance cache
  }

  /**
   * Get default TTL for key type
   */
  getDefaultTTL(key) {
    for (const [prefix, ttl] of Object.entries(this.ttlDefaults)) {
      if (key.includes(prefix)) {
        return ttl;
      }
    }
    return 5; // Default 5 minutes
  }

  /**
   * Set item using appropriate cache strategy
   */
  set(key, data, ttlMinutes = null) {
    const strategy = this.getCacheStrategy(key);
    const ttl = ttlMinutes || this.getDefaultTTL(key);

    if (strategy === 'critical') {
      this.setCritical(key, data, ttl);
    } else {
      this.setPerformance(key, data, ttl);
    }
  }

  /**
   * Set item in critical data cache (stored in localStorage)
   */
  setCritical(key, data, ttlMinutes) {
    const item = {
      data,
      timestamp: Date.now(),
      ttl: ttlMinutes * 60 * 1000,
    };

    // Store in localStorage for persistence
    this.saveCriticalDataToStorage(key, item);
  }

  /**
   * Set item in performance cache (LRU eviction, memory only)
   */
  setPerformance(key, data, ttlMinutes) {
    // Clean up expired items first
    this.cleanupExpired();

    // LRU eviction if at capacity
    if (this.performanceCache.size >= this.maxPerformanceItems) {
      this.evictLRU();
    }

    this.performanceCache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttlMinutes * 60 * 1000,
    });

    // Update access order
    this.updateAccessOrder(key);
  }

  /**
   * Get item using appropriate cache strategy
   */
  get(key) {
    const strategy = this.getCacheStrategy(key);

    if (strategy === 'critical') {
      return this.getCritical(key);
    } else {
      return this.getPerformance(key);
    }
  }

  /**
   * Get item from critical data cache (localStorage with sessionStorage fallback for Safari)
   */
  getCritical(key) {
    if (!this.localStorageAvailable) {
      // Try sessionStorage as fallback for Safari
      return this.getFromSessionStorage(key);
    }

    try {
      const storageKey = this.localStoragePrefix + key;
      const storedData = localStorage.getItem(storageKey);

      if (!storedData) {
        // Try sessionStorage as fallback
        return this.getFromSessionStorage(key);
      }

      const item = JSON.parse(storedData);

      // Check expiration
      const isExpired = Date.now() - item.timestamp > item.ttl;
      if (isExpired) {
        // Remove expired item
        this.removeCriticalDataFromStorage(key);
        return null;
      }

      return item.data;
    } catch (e) {
      console.warn(`Failed to load critical data from localStorage for key: ${key}`, e);
      // Remove corrupted data
      this.removeCriticalDataFromStorage(key);
      return null;
    }
  }

  /**
   * Get item from performance cache (memory only)
   */
  getPerformance(key) {
    const item = this.performanceCache.get(key);

    if (!item) return null;

    const isExpired = Date.now() - item.timestamp > item.ttl;
    if (isExpired) {
      this.performanceCache.delete(key);
      this.removeFromAccessOrder(key);
      return null;
    }

    // Update access order for LRU
    this.updateAccessOrder(key);

    return item.data;
  }

  /**
   * Update access order for LRU eviction
   */
  updateAccessOrder(key) {
    // Remove from current position
    this.removeFromAccessOrder(key);
    // Add to end (most recently used)
    this.accessOrder.push(key);
  }

  /**
   * Remove key from access order
   */
  removeFromAccessOrder(key) {
    const index = this.accessOrder.indexOf(key);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
    }
  }

  /**
   * Evict least recently used item
   */
  evictLRU() {
    if (this.accessOrder.length === 0) return;

    const lruKey = this.accessOrder.shift(); // Remove first item (least recently used)
    this.performanceCache.delete(lruKey);
  }

  /**
   * Clean up expired items from both caches
   */
  cleanupExpired() {
    const now = Date.now();
    let criticalCleaned = 0;
    let performanceCleaned = 0;

    // Clean critical data (localStorage only)
    if (this.localStorageAvailable) {
      try {
        const criticalKeys = Object.keys(localStorage).filter(key =>
          key.startsWith(this.localStoragePrefix),
        );

        criticalKeys.forEach(storageKey => {
          try {
            const storedData = localStorage.getItem(storageKey);
            if (storedData) {
              const item = JSON.parse(storedData);
              if (now - item.timestamp > item.ttl) {
                localStorage.removeItem(storageKey);
                criticalCleaned++;
              }
            }
          } catch (e) {
            // Remove corrupted data
            localStorage.removeItem(storageKey);
            criticalCleaned++;
          }
        });
      } catch (e) {
        console.warn('Failed to clean up critical data:', e);
      }
    }

    // Clean performance data (memory only)
    const expiredKeys = [];
    for (const [key, item] of this.performanceCache) {
      if (now - item.timestamp > item.ttl) {
        expiredKeys.push(key);
      }
    }

    expiredKeys.forEach(key => {
      this.performanceCache.delete(key);
      this.removeFromAccessOrder(key);
      performanceCleaned++;
    });
  }

  /**
   * Setup periodic cleanup every 5 minutes
   */
  setupPeriodicCleanup() {
    setInterval(() => {
      this.cleanupExpired();
    }, 5 * 60 * 1000); // 5 minutes
  }

  // Keep existing methods for backward compatibility
  getAppData() {
    try {
      return this.get('SPOTLIGHT_GET_APP_LOAD');
    } catch (error) {
      console.warn(`Failed to get app data:`, error);
      return null;
    }
  }

  getApiUrl() {
    try {
      return (window.API_BASE_URL || process.env.REACT_APP_DSP_API_URL) + '/';
    } catch (error) {
      console.warn(`Failed to get API URL:`, error);
      return 'https://api.myspotlight.tv/';
    }
  }

  getImageCdnUrl() {
    try {
      const cache = this.getAppData();
      return cache?.app_data?.image_cdn_url || window.IMAGES_BASE_URL;
    } catch (error) {
      console.warn(`Failed to get image CDN URL:`, error);
      return 'https://defaultdspmedia.cachefly.net/images';
    }
  }

  getClientToken() {
    try {
      const cache = this.get('CLIENT_TOKEN');
      return cache;
    } catch (error) {
      console.warn(`Failed to get client token:`, error);
      return null;
    }
  }

  setClientToken(clientToken) {
    if (clientToken) {
      // Client tokens expire every 8 days on the server
      // Set expiration to 7 days to refresh proactively before actual expiration
      const expirationTime = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
      this.set('CLIENT_TOKEN', clientToken, 10080); // 7 days cache
      this.set('CLIENT_TOKEN_EXPIRY', Date.now() + expirationTime, 10080); // 7 days expiry (tokens expire every 8 days on server)
    }
  }

  isClientTokenExpired() {
    try {
      const expiry = this.get('CLIENT_TOKEN_EXPIRY');
      if (!expiry) {
        return true; // No expiry set, consider it expired
      }
      return Date.now() > expiry;
    } catch (error) {
      console.warn('Failed to check client token expiry:', error);
      return true; // Consider expired on error
    }
  }

  /**
   * Get profile token from cache
   * @param {string} profileId - Profile ID
   * @returns {string|null} Profile token or null if not found
   */
  getProfileToken(profileId) {
    try {
      const cache = this.get(`PROFILE_TOKEN_${profileId}`);
      return cache;
    } catch (error) {
      console.warn(`Failed to get profile token for ${profileId}:`, error);
      return null;
    }
  }

  /**
   * Set profile token in cache with expiration
   * @param {string} profileId - Profile ID
   * @param {string} profileToken - Profile token
   */
  setProfileToken(profileId, profileToken) {
    if (profileToken) {
      // Profile tokens expire every 8 days on the server
      // Set expiration to 7 days to refresh proactively before actual expiration
      const expirationTime = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
      this.set(`PROFILE_TOKEN_${profileId}`, profileToken, 10080); // 7 days cache
      this.set(`PROFILE_TOKEN_EXPIRY_${profileId}`, Date.now() + expirationTime, 10080); // 7 days expiry (tokens expire every 8 days on server)

      this.getProfileToken(profileId);
    }
  }

  /**
   * Check if profile token is expired
   * @param {string} profileId - Profile ID
   * @returns {boolean} True if token is expired or not found
   */
  isProfileTokenExpired(profileId) {
    try {
      const expiry = this.get(`PROFILE_TOKEN_EXPIRY_${profileId}`);
      if (!expiry) {
        return true; // No expiry set, consider it expired
      }
      return Date.now() > expiry;
    } catch (error) {
      console.warn(`Failed to check profile token expiry for ${profileId}:`, error);
      return true; // Consider expired on error
    }
  }

  /**
   * Clear profile token from cache
   * @param {string} profileId - Profile ID
   */
  clearProfileToken(profileId) {
    this.invalidate(`PROFILE_TOKEN_${profileId}`);
    this.invalidate(`PROFILE_TOKEN_EXPIRY_${profileId}`);
  }

  /**
   * Clear all profile tokens from cache
   */
  clearAllProfileTokens() {
    const allKeys = this.getAllKeys();
    allKeys.forEach(key => {
      if (key.startsWith('PROFILE_TOKEN_')) {
        this.invalidate(key);
      }
    });
  }

  /**
   * Remove item from appropriate cache
   */
  invalidate(key) {
    const strategy = this.getCacheStrategy(key);

    if (strategy === 'critical') {
      // Remove from localStorage
      this.removeCriticalDataFromStorage(key);
    } else {
      this.performanceCache.delete(key);
      this.removeFromAccessOrder(key);
    }
  }

  /**
   * Clear all cache
   */
  clear() {
    this.performanceCache.clear();
    this.accessOrder.length = 0;
    this.clearCriticalDataFromStorage();
  }

  /**
   * Clear only performance cache (preserve critical data)
   */
  clearPerformance() {
    this.performanceCache.clear();
    this.accessOrder.length = 0;
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      critical: {
        localStorageAvailable: this.localStorageAvailable,
        localStorageKeys: this.getCriticalKeys().length,
      },
      performance: {
        items: this.performanceCache.size,
        maxItems: this.maxPerformanceItems,
        size: this.getCacheSize(this.performanceCache),
      },
      accessOrder: {
        length: this.accessOrder.length,
      },
    };
  }

  /**
   * Calculate cache size in bytes
   */
  getCacheSize(cacheMap) {
    let totalBytes = 0;

    for (const [key, item] of cacheMap) {
      try {
        const jsonSize = JSON.stringify(item).length;
        totalBytes += jsonSize;
      } catch (e) {
        console.warn(`Cannot measure size of key: ${key}`);
      }
    }

    return {
      bytes: totalBytes,
      kb: (totalBytes / 1024).toFixed(2),
      mb: (totalBytes / 1024 / 1024).toFixed(2),
    };
  }

  /**
   * Force cleanup of both caches
   */
  cleanup() {
    this.cleanupExpired();
  }

  /**
   * Get all cache keys from both caches
   */
  getAllKeys() {
    const keys = new Set();

    // Get keys from critical cache (localStorage)
    const criticalKeys = this.getCriticalKeys();
    criticalKeys.forEach(key => keys.add(key));

    // Get keys from performance cache
    for (const key of this.performanceCache.keys()) {
      keys.add(key);
    }

    return Array.from(keys);
  }

  /**
   * Get critical data keys only
   */
  getCriticalKeys() {
    if (!this.localStorageAvailable) {
      return [];
    }

    try {
      const criticalKeys = Object.keys(localStorage).filter(key =>
        key.startsWith(this.localStoragePrefix),
      );

      return criticalKeys.map(storageKey => storageKey.replace(this.localStoragePrefix, ''));
    } catch (e) {
      console.warn('Failed to get critical keys from localStorage:', e);
      return [];
    }
  }

  /**
   * Get performance cache keys only
   */
  getPerformanceKeys() {
    return Array.from(this.performanceCache.keys());
  }

  /**
   * Remove item from cache (alias for invalidate for compatibility)
   */
  remove(key) {
    this.invalidate(key);
  }

  /**
   * Clear a key from both caches
   */
  clearKey(key) {
    this.performanceCache.delete(key);
    this.removeFromAccessOrder(key);
    this.removeCriticalDataFromStorage(key);
  }

  /**
   * Check if key exists in critical cache
   */
  hasCritical(key) {
    if (!this.localStorageAvailable) {
      return false;
    }

    try {
      const storageKey = this.localStoragePrefix + key;
      return localStorage.getItem(storageKey) !== null;
    } catch (e) {
      return false;
    }
  }

  /**
   * Check if key exists in performance cache
   */
  hasPerformance(key) {
    return this.performanceCache.has(key);
  }

  /**
   * Get cache strategy for a key
   */
  getKeyStrategy(key) {
    return this.getCacheStrategy(key);
  }

  /**
   * Get localStorage availability status
   */
  isLocalStorageAvailable() {
    return this.localStorageAvailable;
  }

  // Profile-specific convenience methods
  /**
   * Save current profile to cache
   * @param {Object} profile - Profile object to save
   */
  saveCurrentProfile(profile) {
    if (profile) {
      this.set('CURRENT_PROFILE', profile, 720); // 12 hours
    } else {
      this.invalidate('CURRENT_PROFILE');
    }
  }

  /**
   * Get current profile from cache
   * @returns {Object|null} Current profile or null if not found
   */
  getCurrentProfile() {
    return this.get('CURRENT_PROFILE');
  }

  /**
   * Save profiles list to cache
   * @param {Array} profiles - Array of profile objects
   */
  saveProfiles(profiles) {
    this.set('PROFILES_LIST', profiles, 1440); // 24 hours
  }

  /**
   * Get profiles list from cache
   * @returns {Array|null} Profiles array or null if not found
   */
  getProfiles() {
    return this.get('PROFILES_LIST');
  }

  /**
   * Clear all profile-related cache
   */
  clearProfileCache() {
    this.invalidate('CURRENT_PROFILE');
    this.invalidate('PROFILES_LIST');
  }

  /**
   * Check if movies page data has been preloaded
   * @returns {boolean} True if movies data is cached
   */
  hasMoviesPage() {
    const cacheKey = 'header_movies';
    return this.get(cacheKey) !== null;
  }

  /**
   * Check if series page data has been preloaded
   * @returns {boolean} True if series data is cached
   */
  hasSeriesPage() {
    const cacheKey = 'header_series';
    return this.get(cacheKey) !== null;
  }

  /**
   * Set preloaded data for movies page
   * @param {Object} data - The data to cache
   * @param {number} ttlMinutes - Time to live in minutes (default: 5 minutes)
   */
  setMoviesPage(data, ttlMinutes = 5) {
    const cacheKey = 'header_movies';
    this.set(cacheKey, data, ttlMinutes);
  }

  /**
   * Set preloaded data for series page
   * @param {Object} data - The data to cache
   * @param {number} ttlMinutes - Time to live in minutes (default: 5 minutes)
   */
  setSeriesPage(data, ttlMinutes = 5) {
    const cacheKey = 'header_series';
    this.set(cacheKey, data, ttlMinutes);
  }

  /**
   * Clear all critical data for logout
   */
  clearAllCriticalData() {
    // Clear all critical data from localStorage
    this.clearCriticalDataFromStorage();

    // Clear all profile tokens (this is redundant now but kept for explicit clarity)
    this.clearAllProfileTokens();
  }

  /**
   * Clear only authentication tokens while preserving user preferences and other data.
   * Use this when detecting stale/expired tokens causing "Failed to fetch" errors.
   * This is less aggressive than clearAllCriticalData() and preserves:
   * - Watch history / resumption data
   * - User preferences
   * - Cached content data
   */
  clearAuthTokensOnly() {
    console.log('🔐 CacheService: Clearing auth tokens only (preserving user data)...');
    
    // Clear client token and expiry
    this.invalidate('CLIENT_TOKEN');
    this.invalidate('CLIENT_TOKEN_EXPIRY');
    
    // Clear all profile tokens
    this.clearAllProfileTokens();
    
    // Clear current profile (will be reloaded on next login)
    this.invalidate('CURRENT_PROFILE');
    this.invalidate('PROFILES_LIST');
    
    // NOTE: We intentionally do NOT clear:
    // - USER (comes from Auth0, needed for re-auth flow)
    // - ACCESS_TOKEN / TOKEN (Auth0 manages these)
    // These will be refreshed automatically by Auth0 on next page load
    
    console.log('🔐 CacheService: Auth tokens cleared. User will need to re-authenticate.');
  }

  /**
   * Check if we should clear auth tokens based on error pattern.
   * Returns true if the error suggests stale/expired tokens.
   * @param {Error} error - The error that occurred
   * @returns {boolean} True if auth tokens should be cleared
   */
  shouldClearAuthTokens(error) {
    if (!error) return false;
    
    const errorMessage = error.message || '';
    
    // Network-level failures during authenticated requests usually mean token issues
    const networkFailurePatterns = [
      'Failed to fetch',
      'NetworkError',
      'Network request failed',
      'Load failed',
      'fetch failed',
    ];
    
    return networkFailurePatterns.some(pattern => 
      errorMessage.toLowerCase().includes(pattern.toLowerCase())
    );
  }
}

export const cacheService = new CacheService();
window.cacheService = cacheService;
